import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Pagination,
  Tooltip,
  Card,
  CardContent
} from '@mui/material'
import {
  Search,
  Clear,
  Info,
  Add,
  Visibility
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import medicalCodingService from '../../../services/medicalCodingService'

const ICDSearch = ({ onSelect, version = 'ICD10' }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedCode, setSelectedCode] = useState(null)
  const [codeDetails, setCodeDetails] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [recentSearches, setRecentSearches] = useState([])

  useEffect(() => {
    // Load recent searches from localStorage
    const recent = JSON.parse(localStorage.getItem('icd_recent_searches') || '[]')
    setRecentSearches(recent.slice(0, 5))
  }, [])

  const handleSearch = async (query = searchQuery) => {
    if (!query || query.length < 3) {
      enqueueSnackbar('Please enter at least 3 characters', { variant: 'warning' })
      return
    }

    setLoading(true)
    try {
      const response = await medicalCodingService.searchICDCodes({
        query,
        version,
        limit: 20,
        page
      })

      if (response.data.success) {
        setSearchResults(response.data.data)
        
        // Save to recent searches
        const recent = JSON.parse(localStorage.getItem('icd_recent_searches') || '[]')
        const newRecent = [query, ...recent.filter(item => item !== query)].slice(0, 5)
        localStorage.setItem('icd_recent_searches', JSON.stringify(newRecent))
        setRecentSearches(newRecent)
      }
    } catch (error) {
      enqueueSnackbar('Failed to search ICD codes', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleCodeSelect = async (code) => {
    try {
      // Get detailed information about the code
      const response = await medicalCodingService.getICDCodeDetails(code.code)
      setCodeDetails(response.data.data)
      setSelectedCode(code)
    } catch (error) {
      // If details not available, just select the code
      setSelectedCode(code)
      setCodeDetails(null)
    }
  }

  const handleAddCode = () => {
    if (selectedCode && onSelect) {
      onSelect(selectedCode)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults([])
    setSelectedCode(null)
    setCodeDetails(null)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {version} Code Search
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="Search ICD codes"
            placeholder="Enter code or description (e.g., I10, hypertension)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearch} size="small">
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            disabled={loading || searchQuery.length < 3}
            sx={{ minWidth: 120 }}
          >
            Search
          </Button>
        </Box>

        {/* Recent Searches */}
        {recentSearches.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Recent searches:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {recentSearches.map((recent, index) => (
                <Chip
                  key={index}
                  label={recent}
                  size="small"
                  onClick={() => {
                    setSearchQuery(recent)
                    handleSearch(recent)
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        {/* Search Results */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle1" gutterBottom>
            Search Results ({searchResults.length})
          </Typography>
          
          {searchResults.length === 0 && !loading ? (
            <Alert severity="info">
              {searchQuery ? 'No results found. Try different keywords.' : 'Enter search terms to find ICD codes'}
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell width={100}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((result, index) => (
                    <TableRow 
                      key={index}
                      hover
                      selected={selectedCode?.code === result.code}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleCodeSelect(result)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {result.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {result.description}
                        </Typography>
                        {result.chapter && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Chapter: {result.chapter}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleCodeSelect(result)
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Select Code">
                            <IconButton 
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (onSelect) onSelect(result)
                              }}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Code Details */}
        {selectedCode && (
          <Box sx={{ width: 350, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" gutterBottom>
              Code Details
            </Typography>
            
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  {selectedCode.code}
                </Typography>
                
                <Typography variant="body1" gutterBottom>
                  {selectedCode.description}
                </Typography>

                {codeDetails && (
                  <Box sx={{ mt: 2 }}>
                    {codeDetails.chapter && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Chapter
                        </Typography>
                        <Typography variant="body2">
                          {codeDetails.chapter} - {codeDetails.chapterTitle}
                        </Typography>
                      </Box>
                    )}

                    {codeDetails.block && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Block
                        </Typography>
                        <Typography variant="body2">
                          {codeDetails.block} - {codeDetails.blockTitle}
                        </Typography>
                      </Box>
                    )}

                    {codeDetails.inclusions && codeDetails.inclusions.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Inclusions
                        </Typography>
                        {codeDetails.inclusions.map((inclusion, index) => (
                          <Typography key={index} variant="body2" sx={{ ml: 1 }}>
                            • {inclusion}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {codeDetails.exclusions && codeDetails.exclusions.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Exclusions
                        </Typography>
                        {codeDetails.exclusions.map((exclusion, index) => (
                          <Typography key={index} variant="body2" sx={{ ml: 1 }}>
                            • {exclusion}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {codeDetails.notes && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Notes
                        </Typography>
                        <Typography variant="body2">
                          {codeDetails.notes}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddCode}
                  sx={{ mt: 2 }}
                >
                  Select This Code
                </Button>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ICDSearch